/**
 * Generative-UI spike — the renderer.
 *
 * Walks a UISpec tree and renders each node from the catalog. Unknown components
 * render a visible, harmless fallback (the reliability guardrail — the agent can
 * never inject arbitrary UI). Swapping in json-render means replacing this file
 * with its engine; the catalog + spec stay yours.
 */
import React from 'react';
import { UISpec } from './uiSpec';
import { buildCatalog, ActionHandler } from './catalog';
import { InlineCard, CARD_TYPES } from './InlineCard';

function Unsupported({ type }: { type: string }) {
  // Monochrome + quiet (the product is grayscale). Visible only in dev as a gentle hint; it
  // should rarely show because callers render known spec types explicitly.
  if (process.env.NODE_ENV === 'production') return null;
  return (
    <div className="rounded border border-dashed border-secondary-300 dark:border-secondary-600 bg-secondary-50 dark:bg-secondary-800 px-2 py-1 text-xs text-secondary-500 dark:text-secondary-400">
      Unsupported component: <code>{type}</code>
    </div>
  );
}

export function JsonRenderer({ spec, onAction }: { spec: UISpec; onAction?: ActionHandler }) {
  const catalog = React.useMemo(() => buildCatalog(onAction || (() => {})), [onAction]);

  function render(node: UISpec, key: React.Key): React.ReactNode {
    if (!node || typeof node.type !== 'string') return null;
    const Comp = catalog[node.type];
    if (!Comp) return <Unsupported key={key} type={node.type} />;
    // children may arrive malformed from arbitrary agent/legacy JSON — guard the shape so a non-array
    // (object/string) can't throw and take down the whole message render (the safe-fallback guarantee).
    const children = (Array.isArray(node.children) ? node.children : []).map((c, i) => render(c, i));
    const inner = <Comp {...(node.props || {})}>{children.length ? children : undefined}</Comp>;
    // Public-facing data cards (charts/tables/artifacts) get the unified <InlineCard/> chrome;
    // inline primitives (Text/Button/…) flow with the prose unwrapped.
    return CARD_TYPES.has(node.type)
      ? <InlineCard key={key} type={node.type}>{inner}</InlineCard>
      : <React.Fragment key={key}>{inner}</React.Fragment>;
  }

  return <>{render(spec, 'root')}</>;
}
